import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { chatAnchorShot, suggestSettings, createAnchorShot, deleteAnchorShot, getShotImageUrl } from "@/services/shotService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Camera, Loader2, Sparkles, Video, Aperture, Lightbulb, Move,
  ChevronDown, ChevronUp, Send, Plus, Trash2, Film, MessageSquare,
  Save, Pencil, Wand2, Play, X
} from "lucide-react";

import { MULTI_FRAME } from "@/constants/shotSettings";
import MiniSelect from "@/components/storyboard/MiniSelect";
import CineIcon from "@/components/ui/CineIcon";

function ShotThumbnail({ shot, anchorId, onUpdate, onDelete, shotImageUrl }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsg, setChatMsg] = useState("");
  const [editing, setEditing] = useState(false);

  const handleChat = async () => {
    if (!chatMsg.trim() || editing) return;
    setEditing(true);
    try {
      const data = await chatAnchorShot(anchorId, shot.id, { message: chatMsg });
      onUpdate(data);
      setChatMsg("");
      toast.success("Shot updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Edit failed");
    } finally { setEditing(false); }
  };

  const s = shot.settings || {};
  return (
    <div className="bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)] overflow-hidden">
      <div className="relative aspect-video bg-[var(--bg-muted)]">
        <img src={shot.image_data ? `data:image/jpeg;base64,${shot.image_data}` : shotImageUrl} alt="Shot" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <div className="flex flex-wrap gap-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(212,168,83,0.3)] text-[var(--color-gold-400)]">{s.shot_type}</span>
            {shot.keyframe_label && shot.keyframe_label !== "Single" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(20,184,166,0.3)] text-[var(--color-teal-400)]">{shot.keyframe_label}</span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">{shot.id}</span>
          </div>
        </div>
        <button onClick={() => onDelete(shot.id)} className="absolute top-1.5 right-1.5 p-1 bg-black/50 rounded text-red-400 hover:bg-red-500/40 opacity-0 hover:opacity-100 transition-opacity" data-testid="delete-shot-btn">
          <Trash2 size={10} />
        </button>
      </div>
      <div className="p-2">
        <Button onClick={() => setChatOpen(!chatOpen)} variant="ghost" size="sm" className={`w-full text-[10px] h-7 ${chatOpen ? 'text-[var(--color-gold-400)]' : 'text-[var(--text-muted)]'}`} data-testid="chat-edit-btn">
          <MessageSquare size={11} /> <span className="ml-1">Edit</span>
        </Button>
        <AnimatePresence>
          {chatOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex gap-1.5 mt-2">
                <Textarea value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} placeholder="Make it more dramatic..." className="input resize-none h-12 text-[10px] flex-1" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }} />
                <Button onClick={handleChat} disabled={editing || !chatMsg.trim()} size="sm" className="btn-primary self-end h-8 w-8 p-0" data-testid="send-edit-btn">
                  {editing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function AnchorShotPanel({
  anchor,
  onClose,
  onUpdate,
  onOpenQuickDraw,
  onOpenBlocking,
  SHOT_TYPES, CAMERA_ANGLES, CAMERA_MOVEMENTS, LIGHTING_STYLES, LENS_OPTIONS,
}) {
  const [prompt, setPrompt] = useState("");
  const [shotType, setShotType] = useState("medium");
  const [cameraAngle, setCameraAngle] = useState("eye-level");
  const [cameraMovement, setCameraMovement] = useState("static");
  const [lighting, setLighting] = useState("natural");
  const [lens, setLens] = useState("50mm");
  const [generating, setGenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiReasoning, setAiReasoning] = useState(null);
  const [showSettings, setShowSettings] = useState(true);
  const [activeKeyframe, setActiveKeyframe] = useState(0);
  const [localShots, setLocalShots] = useState([]);  // Local shots state for immediate updates

  const kfConfig = MULTI_FRAME[cameraMovement] || { count: 1, labels: ["Single"] };

  // Sync local shots with anchor.shots
  useEffect(() => {
    if (anchor?.shots) {
      setLocalShots(anchor.shots);
    } else {
      setLocalShots([]);
    }
  }, [anchor?.shots, anchor?.id]);

  // Pre-fill from planned settings (auto-plan)
  useEffect(() => {
    if (!anchor) return;
    const p = anchor.planned_settings;
    if (p) {
      setShotType(p.shot_type || "medium");
      setCameraAngle(p.camera_angle || "eye-level");
      setCameraMovement(p.camera_movement || "static");
      setLighting(p.lighting || "natural");
      setLens(p.lens || "50mm");
      setPrompt(p.prompts?.[0] || "");
    } else {
      setPrompt(anchor.selected_text?.slice(0, 200) || "");
    }
    setActiveKeyframe(0);
    setShowSettings(!(anchor.shots?.length > 0));
    setAiReasoning(null);
  }, [anchor?.id]);

  // AI Suggest shot settings
  const handleAiSuggest = async () => {
    if (!anchor?.selected_text) return;
    setSuggesting(true);
    setAiReasoning(null);
    try {
      const data = await suggestSettings({
        selected_text: anchor.selected_text,
        scene_context: ""
      });
      
      if (data.shot_type) setShotType(data.shot_type);
      if (data.camera_angle) setCameraAngle(data.camera_angle);
      if (data.camera_movement) setCameraMovement(data.camera_movement);
      if (data.lighting) setLighting(data.lighting);
      if (data.lens) {
        const lensMap = {
          "wide-16mm": "16mm", "wide-24mm": "24mm", "standard-35mm": "35mm",
          "normal-50mm": "50mm", "portrait-85mm": "85mm", "telephoto-135mm": "135mm"
        };
        setLens(lensMap[data.lens] || "50mm");
      }
      if (data.prompt_suggestion) setPrompt(data.prompt_suggestion);
      setAiReasoning(data.reasoning);
      
      toast.success("AI suggestions applied!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !anchor) return;
    setGenerating(true);
    try {
      const newShot = await createAnchorShot(anchor.id, {
        prompt,
        shot_type: shotType,
        camera_angle: cameraAngle,
        camera_movement: cameraMovement,
        lighting,
        lens,
        keyframe_index: activeKeyframe,
      });

      setLocalShots(prev => [...prev, newShot]);
      setShowSettings(false);
      
      // Also sync with parent
      onUpdate?.();

      if (kfConfig.count > 1 && activeKeyframe < kfConfig.count - 1) {
        const nextIdx = activeKeyframe + 1;
        setActiveKeyframe(nextIdx);
        setShowSettings(true);
        const planned = anchor.planned_settings;
        if (planned?.prompts?.[nextIdx]) setPrompt(planned.prompts[nextIdx]);
        toast.success(`${kfConfig.labels[activeKeyframe]} frame done. Now: ${kfConfig.labels[nextIdx]}`);
      } else {
        setActiveKeyframe(0);
        toast.success("Shot generated!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const handleDeleteShot = async (shotId) => {
    try {
      await deleteAnchorShot(anchor.id, shotId);
      setLocalShots(prev => prev.filter(s => s.id !== shotId));
      onUpdate?.();
      toast.success("Shot deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleShotUpdate = (updatedShot) => {
    // Update local state immediately with the updated shot
    if (updatedShot) {
      setLocalShots(prev => prev.map(s => s.id === updatedShot.id ? updatedShot : s));
    }
    onUpdate?.();
  };

  if (!anchor) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-[240px]">
          <CineIcon icon={Camera} size={60} className="mx-auto mb-4" />
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-gold-500)] mb-1.5">Shot Designer</div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-2">Design your first shot</h3>
          <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-4">
            Select text in the script and click <strong className="text-[var(--text-secondary)]">+ Add Shot</strong> to start designing, or use <strong className="text-[var(--text-secondary)]">Auto Shot Plan</strong>.
          </p>
          {onOpenQuickDraw && (
            <Button 
              onClick={onOpenQuickDraw} 
              className="btn-secondary text-xs w-full"
              size="sm"
              data-testid="quick-draw-btn-empty"
            >
              <Pencil size={14} />
              <span className="ml-1.5">Quick Draw a Shot</span>
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Use localShots for immediate updates
  const shots = localShots;

  return (
    <div className="h-full flex flex-col" data-testid="anchor-shot-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-mono text-[var(--color-gold-400)] bg-[rgba(212,168,83,0.1)] px-2 py-0.5 rounded">{anchor.id}</span>
          <div className="flex items-center gap-1.5">
            {shots.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">{shots.length} shot{shots.length !== 1 ? 's' : ''}</span>
            )}
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={14} /></button>
          </div>
        </div>
        <p className="text-[10px] text-[var(--text-muted)] line-clamp-2 italic">"{anchor.selected_text?.slice(0, 100)}"</p>
      </div>

      {/* Generated Shots - Always visible at TOP when they exist */}
      {shots.length > 0 && (
        <div className="p-4 border-b border-[var(--border-subtle)] bg-[var(--bg-overlay)] flex-shrink-0 max-h-[60%] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-[var(--color-gold-400)] flex items-center gap-1.5">
              <Sparkles size={12} />
              Generated Shots
            </h4>
            <span className="text-[11px] text-[var(--text-muted)]">{shots.length} shot{shots.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-3">
            {shots.map(shot => (
              <ShotThumbnail key={shot.id} shot={shot} anchorId={anchor.id} shotImageUrl={getShotImageUrl(anchor.id, shot.id)} onUpdate={handleShotUpdate} onDelete={handleDeleteShot} />
            ))}
          </div>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Multi-frame indicator */}
          {kfConfig.count > 1 && showSettings && (
            <div className="flex items-center gap-2 p-2.5 bg-[rgba(20,184,166,0.1)] border border-[rgba(20,184,166,0.3)] rounded-lg">
              <Film size={12} className="text-[var(--color-teal-400)]" />
              <div className="flex gap-1">
                {kfConfig.labels.map((label, i) => (
                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    i === activeKeyframe ? 'bg-[var(--color-teal-500)] text-black'
                    : i < activeKeyframe ? 'bg-[rgba(20,184,166,0.3)] text-[var(--color-teal-400)]'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-muted)]'
                  }`}>{label}</span>
                ))}
              </div>
            </div>
          )}

          {/* Add shot button when shots exist but settings hidden */}
          {shots.length > 0 && !showSettings && (
            <Button onClick={() => setShowSettings(true)} className="w-full btn-secondary text-xs" size="sm" data-testid="add-more-shots-btn">
              <Plus size={13} /> <span className="ml-1.5">Add Another Shot</span>
            </Button>
          )}

          {/* Settings panel */}
          <AnimatePresence>
            {showSettings && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="space-y-4">
                  {shots.length > 0 && (
                    <div className="flex justify-end">
                      <button onClick={() => { setShowSettings(false); setActiveKeyframe(0); }} className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center gap-1">
                        <ChevronUp size={10} /> Hide
                      </button>
                    </div>
                  )}

                  {/* AI Suggest Button */}
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={handleAiSuggest} 
                      disabled={suggesting || !anchor?.selected_text}
                      className="flex-1 btn-secondary text-xs"
                      size="sm"
                      data-testid="ai-suggest-btn"
                    >
                      {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                      <span className="ml-1.5">{suggesting ? 'Analyzing...' : 'AI Suggest Settings'}</span>
                    </Button>
                  </div>
                  
                  {/* AI Reasoning */}
                  {aiReasoning && (
                    <div className="bg-[rgba(212,168,83,0.06)] border border-[rgba(212,168,83,0.2)] rounded-lg p-2.5 space-y-1.5">
                      <p className="text-[11px] font-medium text-[var(--color-gold-400)] flex items-center gap-1">
                        <Wand2 size={10} /> AI Recommendation
                      </p>
                      {aiReasoning.why_shot_type && (
                        <p className="text-[11px] text-[var(--text-muted)]">
                          <span className="text-[var(--text-primary)]">Shot:</span> {aiReasoning.why_shot_type}
                        </p>
                      )}
                      {aiReasoning.why_movement && (
                        <p className="text-[11px] text-[var(--text-muted)]">
                          <span className="text-[var(--text-primary)]">Movement:</span> {aiReasoning.why_movement}
                        </p>
                      )}
                      {aiReasoning.emotional_intent && (
                        <p className="text-[11px] text-[var(--text-muted)]">
                          <span className="text-[var(--text-primary)]">Intent:</span> {aiReasoning.emotional_intent}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="font-mono text-[11px] text-[var(--text-muted)] uppercase tracking-[0.18em] mb-1.5 block">Shot Description</label>
                    <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the shot..." className="input resize-none h-16 text-xs" data-testid="shot-prompt" />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <MiniSelect options={SHOT_TYPES} value={shotType} onChange={setShotType} label="Shot" icon={Video} />
                    <MiniSelect options={CAMERA_ANGLES} value={cameraAngle} onChange={setCameraAngle} label="Angle" icon={Move} />
                    <MiniSelect options={CAMERA_MOVEMENTS} value={cameraMovement} onChange={(v) => { setCameraMovement(v); setActiveKeyframe(0); }} label="Movement" icon={Move} />
                    <MiniSelect options={LIGHTING_STYLES} value={lighting} onChange={setLighting} label="Light" icon={Lightbulb} />
                    <div className="col-span-2">
                      <MiniSelect options={LENS_OPTIONS} value={lens} onChange={setLens} label="Lens" icon={Aperture} />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>
      
      {/* Sticky Footer - Action Buttons */}
      {showSettings && (
        <div className="p-4 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0 space-y-2">
          <div className="flex gap-2">
            {/* Quick Draw button */}
            {onOpenQuickDraw && (
              <Button onClick={onOpenQuickDraw} className="btn-secondary flex-1" data-testid="quick-draw-btn">
                <Pencil size={15} />
                <span className="ml-2">Quick Draw</span>
              </Button>
            )}
            {/* Design Blocking button */}
            {onOpenBlocking && (
              <Button onClick={onOpenBlocking} className="btn-secondary flex-1" data-testid="design-blocking-btn">
                <Camera size={15} />
                <span className="ml-2">3D Blocking</span>
              </Button>
            )}
          </div>
          <Button onClick={handleGenerate} disabled={generating || !prompt.trim()} className={`btn-primary w-full ${!generating && prompt.trim() ? 'cine-shimmer' : ''}`} data-testid="generate-shot-btn">
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            <span className="ml-2">{generating ? 'Generating...' : kfConfig.count > 1 ? `Generate ${kfConfig.labels[activeKeyframe]} Frame` : 'Generate Shot'}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
